import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	MagnifyingGlassIcon,
	ExclamationTriangleIcon,
	LockClosedIcon,
	PhoneIcon,
	EnvelopeIcon,
	ListBulletIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader } from '../../components/ui';
import StaffInvoicePanel from '../../components/staff/StaffInvoicePanel';
import { staffProfileApi, ApiError } from '../../api';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSION } from '../../types/permissions';
import type {
	StaffBooking,
	StaffBookingsResponse,
	StaffBookingTab,
	StaffNextBooking,
	StaffSalon,
} from '../../types/staffBookings';
import {
	bookingDateKey,
	bookingTab,
	formatClock,
	formatDay,
	isCompletedForStaff,
	monthStart,
	startMinutes,
	todayKey,
} from '../../utils/staffCalendar';
import BookingDetailModal from './BookingDetailModal';
import BookingsCalendar, {
	calendarFetchRange,
	defaultCalendarPrefs,
	type CalendarPrefs,
} from './BookingsCalendar';
import { taskBadgeClass } from './BookingPopover';

// Tab configuration
const TABS: { key: StaffBookingTab; label: string }[] = [
	{ key: 'today', label: 'Today' },
	{ key: 'upcoming', label: 'Upcoming' },
	{ key: 'previous', label: 'Previous' },
	{ key: 'completed', label: 'Completed' },
	{ key: 'cancelled', label: 'Cancelled' },
];

type ViewMode = 'list' | 'calendar';

const PAGE_SIZE = 20;
const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

// Status-based left border colors
const STATUS_BORDER_COLORS: Record<string, string> = {
	CONFIRMED: 'border-l-emerald-500',
	PENDING: 'border-l-amber-500',
	IN_PROGRESS: 'border-l-blue-500',
	COMPLETED: 'border-l-emerald-500',
	CANCELLED: 'border-l-red-500',
};

// Format currency
const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

// Format time from appointmentDateTime string
const formatTime = (dateTimeStr: string): { time: string; period: string } => {
	try {
		const date = new Date(dateTimeStr);
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const period = hours >= 12 ? 'PM' : 'AM';
		const displayHours = hours % 12 || 12;
		const displayMinutes = minutes.toString().padStart(2, '0');
		return { time: `${displayHours}:${displayMinutes}`, period };
	} catch {
		return { time: '-', period: '' };
	}
};

// Format duration from minutes
const formatDuration = (minutes: number): string => {
	if (!minutes) return '';
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	if (hours > 0 && mins > 0) return `${hours}h ${mins.toString().padStart(2, '0')}m`;
	if (hours > 0) return `${hours}h 00m`;
	return `${mins}m`;
};

const StaffBookings = () => {
	const navigate = useNavigate();
	const [view, setView] = useState<ViewMode>('list');
	const [activeTab, setActiveTab] = useState<StaffBookingTab>('upcoming');
	const [bookings, setBookings] = useState<StaffBooking[]>([]);
	const [tabCounts, setTabCounts] = useState({
		today: 0,
		upcoming: 0,
		previous: 0,
		completed: 0,
		cancelled: 0,
	});
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [selectedDate, setSelectedDate] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

	// Paging: the server slices the active tab; Load more appends the next page.
	const [page, setPage] = useState(0);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

	// Shared by both views: the earliest booking still ahead and the salons this person works at.
	const [nextBooking, setNextBooking] = useState<StaffNextBooking | null>(null);
	const [salons, setSalons] = useState<StaffSalon[]>([]);

	// Calendar preferences live here so they survive a switch to List and back.
	const [calendarPrefs, setCalendarPrefs] = useState<CalendarPrefs>(() =>
		defaultCalendarPrefs(todayKey())
	);
	const [calendarBookings, setCalendarBookings] = useState<StaffBooking[]>([]);
	const [isCalendarLoading, setIsCalendarLoading] = useState(false);
	const [calendarError, setCalendarError] = useState<string | null>(null);

	// Invoicing is permission-gated per role (mirrors htmls/Staff/22 oct/staff_permissions):
	// Create = open/review the invoice panel, Send = actually send, Manage All = both for any
	// appointment of the salon. Contact details are masked server-side unless the role also has
	// View Contact Details; the card's "Send to" toggles only pick the channels.
	const { hasAny } = usePermissions();
	const canReviewInvoice = hasAny(PERMISSION.INVOICE_CREATE_OWN, PERMISSION.INVOICE_MANAGE_ALL);
	const canSendInvoice = hasAny(PERMISSION.INVOICE_SEND_OWN, PERMISSION.INVOICE_MANAGE_ALL);
	const [openInvoiceId, setOpenInvoiceId] = useState<number | null>(null);
	const [sendTo, setSendTo] = useState<Record<number, { phone: boolean; email: boolean }>>({});

	const sendToFor = (booking: StaffBooking) =>
		sendTo[booking.id] ?? { phone: !!booking.customerPhone, email: !!booking.customerEmail };

	// Toggle a channel; the last remaining one can't be switched off.
	const toggleSendTo = (booking: StaffBooking, channel: 'phone' | 'email') => {
		const current = sendToFor(booking);
		const other = channel === 'phone' ? 'email' : 'phone';
		if (current[channel] && !current[other]) return;
		setSendTo(prev => ({
			...prev,
			[booking.id]: { ...current, [channel]: !current[channel] },
		}));
	};

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	const applyCounts = (response: StaffBookingsResponse) => {
		setTabCounts({
			today: response.todayCount,
			upcoming: response.upcomingCount,
			previous: response.previousCount,
			completed: response.completedCount,
			cancelled: response.cancelledCount,
		});
		setNextBooking(response.nextBooking ?? null);
		setSalons(response.salons ?? []);
	};

	const listParams = useMemo(
		() => ({
			tab: activeTab,
			search: debouncedSearch.trim() || undefined,
			date: activeTab !== 'today' && selectedDate ? selectedDate : undefined,
		}),
		[activeTab, debouncedSearch, selectedDate]
	);

	// Fetch the first page of the list
	const fetchBookings = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		setLoadMoreError(null);

		try {
			const response = await staffProfileApi.getBookings({
				...listParams,
				page: 0,
				size: PAGE_SIZE,
			});

			setBookings(response.bookings);
			setPage(0);
			setTotal(response.total ?? response.bookings.length);
			setHasMore(!!response.hasMore);
			applyCounts(response);
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load bookings');
		} finally {
			setIsLoading(false);
		}
	}, [listParams]);

	useEffect(() => {
		if (view === 'list') fetchBookings();
	}, [view, fetchBookings]);

	// Append the next page; a record already on screen is never added twice.
	const loadMore = async () => {
		setIsLoadingMore(true);
		setLoadMoreError(null);
		try {
			const response = await staffProfileApi.getBookings({
				...listParams,
				page: page + 1,
				size: PAGE_SIZE,
			});
			setBookings(prev => {
				const seen = new Set(prev.map(b => b.id));
				return [...prev, ...response.bookings.filter(b => !seen.has(b.id))];
			});
			setPage(page + 1);
			setTotal(response.total ?? total);
			setHasMore(!!response.hasMore);
		} catch (err) {
			const apiError = err as ApiError;
			setLoadMoreError(apiError.message || 'Failed to load more bookings');
		} finally {
			setIsLoadingMore(false);
		}
	};

	// Calendar data: every status across the visible period plus the mini calendar's grid.
	const calendarRange = useMemo(() => calendarFetchRange(calendarPrefs), [calendarPrefs]);
	const fetchCalendar = useCallback(async () => {
		setIsCalendarLoading(true);
		setCalendarError(null);
		try {
			const response = await staffProfileApi.getBookings({
				tab: 'calendar',
				search: debouncedSearch.trim() || undefined,
				from: calendarRange.from,
				to: calendarRange.to,
			});
			setCalendarBookings(response.bookings);
			applyCounts(response);
		} catch (err) {
			const apiError = err as ApiError;
			setCalendarError(apiError.message || 'Failed to load the calendar');
		} finally {
			setIsCalendarLoading(false);
		}
	}, [calendarRange.from, calendarRange.to, debouncedSearch]);

	useEffect(() => {
		if (view === 'calendar') fetchCalendar();
	}, [view, fetchCalendar]);

	const updateCalendarPrefs = useCallback(
		(patch: Partial<CalendarPrefs>) => setCalendarPrefs(prev => ({ ...prev, ...patch })),
		[]
	);

	// Opening the calendar with a date selected anchors it there; switching views closes any
	// calendar popover because the calendar unmounts.
	const switchView = (next: ViewMode) => {
		if (next === 'calendar' && selectedDate) {
			updateCalendarPrefs({ anchor: selectedDate, miniMonth: monthStart(selectedDate) });
		}
		setView(next);
	};

	// In the calendar the date picker navigates; in the list it filters.
	const onDateChange = (value: string) => {
		setSelectedDate(value);
		if (value && view === 'calendar') {
			updateCalendarPrefs({ anchor: value, miniMonth: monthStart(value) });
		}
	};

	// From the calendar popover: select the booking's status tab and date, then show the list.
	const showInList = useCallback((booking: StaffBooking) => {
		const tab = bookingTab(booking, todayKey());
		setActiveTab(tab);
		setSelectedDate(tab === 'today' ? '' : bookingDateKey(booking));
		setView('list');
	}, []);

	const openInvoiceFromCalendar = useCallback(
		(booking: StaffBooking) => {
			showInList(booking);
			setOpenInvoiceId(booking.id);
		},
		[showInList]
	);

	// Get border color for booking card based on status
	const getBorderColor = (status: string): string => {
		return STATUS_BORDER_COLORS[status] || 'border-l-gray-300';
	};

	// Get opacity class for completed/cancelled
	const getCardOpacity = (status: string): string => {
		if (status === 'COMPLETED') return 'opacity-85';
		if (status === 'CANCELLED') return 'opacity-70';
		return '';
	};

	// Status badge or the Complete (TCR) action. Today now lists finished bookings too, so the
	// badge follows the booking, not the tab. "Started" comes from the server's salon clock.
	const renderActions = (booking: StaffBooking) => {
		if (booking.status === 'CANCELLED') {
			return (
				<span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
					&#10007; Cancelled
				</span>
			);
		}

		if (isCompletedForStaff(booking)) {
			return (
				<span className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
					&#10003; Completed
				</span>
			);
		}

		if (activeTab === 'today' && booking.started) {
			return (
				<button
					type="button"
					onClick={e => {
						e.stopPropagation();
						navigate(`/staff/tcr/new?appointmentId=${booking.id}`);
					}}
					className="px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2"
					style={{ background: GRADIENT }}
				>
					Complete
				</button>
			);
		}

		return null;
	};

	// Client contact channels. Values arrive masked from the API unless the role has View
	// Contact Details; every staff member sees them. Roles that can invoice get the same chips
	// as "Send to" toggles, picking the channels an invoice goes out on.
	const renderContact = (booking: StaffBooking) => {
		if (!booking.customerPhone && !booking.customerEmail) return null;
		const current = sendToFor(booking);
		const chip = (channel: 'phone' | 'email', value: string, Icon: typeof PhoneIcon) =>
			canReviewInvoice ? (
				<button
					type="button"
					onClick={e => {
						e.stopPropagation();
						toggleSendTo(booking, channel);
					}}
					aria-pressed={current[channel]}
					aria-label={`${channel === 'phone' ? 'Phone' : 'Email'} ${value}, ${current[channel] ? 'sending here' : 'not sending here'}`}
					title={current[channel] ? 'Sending here' : 'Not sending here'}
					className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] ${
						current[channel]
							? 'border-[#667eea] bg-indigo-50 dark:bg-indigo-900/20 text-gray-700 dark:text-gray-200'
							: 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 line-through'
					}`}
				>
					<Icon className="w-3.5 h-3.5" aria-hidden="true" />
					<span className="tracking-wide">{value}</span>
				</button>
			) : (
				<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
					<Icon className="w-3.5 h-3.5" aria-hidden="true" />
					<span className="tracking-wide">{value}</span>
				</span>
			);
		return (
			<div className="flex items-center gap-1.5 flex-wrap mt-2 text-xs text-gray-500 dark:text-gray-400">
				<span>{canReviewInvoice ? 'Send to:' : 'Contact:'}</span>
				{booking.customerPhone && chip('phone', booking.customerPhone, PhoneIcon)}
				{booking.customerEmail && chip('email', booking.customerEmail, EnvelopeIcon)}
				{booking.customerContactMasked && (
					<span
						className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500"
						title="Client contact details are masked. BBP still delivers messages to the real contact."
					>
						<LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" />
						<span>masked</span>
					</span>
				)}
			</div>
		);
	};

	// Invoice action: locked until the appointment is completed (or an invoice already exists),
	// "Invoice Sent" once one has gone out. The permission gate stays; the server enforces it too.
	const renderInvoiceAction = (booking: StaffBooking) => {
		if (!canReviewInvoice || booking.status === 'CANCELLED') return null;
		const state = booking.invoiceState ?? 'NONE';
		const isOpen = openInvoiceId === booking.id;
		if (state === 'NONE' && !isCompletedForStaff(booking)) {
			return (
				<button
					type="button"
					disabled
					title="Available once the appointment is completed"
					aria-label="Invoice, available once the appointment is completed"
					className="inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium border-2 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed"
				>
					<LockClosedIcon className="w-4 h-4" aria-hidden="true" /> Invoice
				</button>
			);
		}
		const sent = state !== 'NONE';
		return (
			<button
				type="button"
				onClick={e => {
					e.stopPropagation();
					setOpenInvoiceId(isOpen ? null : booking.id);
				}}
				aria-expanded={isOpen}
				className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium border-2 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2 ${
					sent
						? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
						: isOpen
							? 'border-[#667eea] bg-indigo-50 dark:bg-indigo-900/20 text-[#667eea]'
							: 'border-[#667eea] text-[#667eea] hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
				}`}
			>
				{sent ? '✓ Invoice Sent' : '🧾 Invoice'}
			</button>
		);
	};

	// Render booking card. appointmentDateTime is salon wall-clock time without an offset, so
	// Date parses it as-is and the card shows the salon's hours wherever the staff member is.
	const renderBookingCard = (booking: StaffBooking) => {
		const { time, period } = formatTime(booking.appointmentDateTime);
		const duration = formatDuration(booking.durationMinutes);
		const yourShare =
			booking.staffTaskMinutes && booking.staffTaskMinutes !== booking.durationMinutes
				? formatDuration(booking.staffTaskMinutes)
				: '';
		const bookingDate = new Date(booking.appointmentDateTime).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
		});
		const serviceDetails = [booking.serviceName, booking.size, booking.hairLength]
			.filter(Boolean)
			.join(' • ');
		const addressLine = [
			booking.location?.address,
			booking.location?.city,
			[booking.location?.state, booking.location?.zipCode].filter(Boolean).join(' '),
		]
			.filter(Boolean)
			.join(', ');
		const openDetail = () => setSelectedBookingId(booking.id);

		return (
			<div
				key={booking.id}
				role="button"
				tabIndex={0}
				aria-label={`${booking.customerName || 'Unknown'}, ${bookingDate} ${time} ${period}, ${serviceDetails || 'service'}. Open booking details`}
				onClick={openDetail}
				onKeyDown={e => {
					if (e.target !== e.currentTarget) return;
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						openDetail();
					}
				}}
				className={`bg-white dark:bg-gray-700/50 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:translate-x-1 hover:shadow-md border-l-4 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${getBorderColor(booking.status)} ${getCardOpacity(booking.status)}`}
			>
				<div className="flex items-center gap-5 flex-wrap md:flex-nowrap">
					{/* Time Block */}
					<div className="text-center min-w-[80px] p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
						<div className="text-xs font-medium text-gray-500 dark:text-gray-400">
							{bookingDate}
						</div>
						<div className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">
							{time} {period}
						</div>
						{duration && (
							<div className="text-xs text-[#667eea] mt-1">
								{duration}
								{booking.durationEstimated ? ' (est.)' : ''}
							</div>
						)}
						{yourShare && (
							<div
								className="text-[11px] text-gray-500 dark:text-gray-400"
								title="Your share of the appointment"
							>
								you: {yourShare}
							</div>
						)}
					</div>

					{/* Info Section */}
					<div className="flex-1 min-w-0">
						<div className="text-base font-semibold text-gray-900 dark:text-white mb-0.5">
							{booking.customerName || 'Unknown'}
						</div>
						<div className="text-xs text-gray-400 dark:text-gray-500 mb-1">
							{booking.providerBusinessName}
							{addressLine && (
								<span>
									{booking.providerBusinessName ? ' · ' : ''}
									{addressLine}
								</span>
							)}
						</div>
						<div className="text-sm text-[#667eea] font-medium mb-1">
							{serviceDetails || '-'}
						</div>

						{/* Task Badges */}
						{booking.assignedTasks && booking.assignedTasks.length > 0 && (
							<div className="flex items-center gap-1.5 mt-2 flex-wrap">
								{booking.assignedTasks.map(taskType => (
									<span
										key={taskType}
										className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${taskBadgeClass(taskType)}`}
									>
										{taskType}
									</span>
								))}
							</div>
						)}

						{/* Masked client contact; hidden on cancelled bookings */}
						{booking.status !== 'CANCELLED' && renderContact(booking)}
					</div>

					{/* Amount + Actions */}
					<div className="flex flex-row items-center justify-between md:flex-col md:items-end gap-2 w-full md:w-auto mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 border-gray-200 dark:border-gray-600">
						<div className="flex flex-col items-end">
							<div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
								{formatCurrency(booking.amount)}
							</div>
							{booking.tipAmount != null && booking.tipAmount > 0 && (
								<p className="text-xs font-medium text-amber-600 dark:text-amber-400">
									+ {formatCurrency(booking.tipAmount)} tip
								</p>
							)}
						</div>
						<div className="flex items-center gap-2 flex-wrap justify-end">
							{renderInvoiceAction(booking)}
							{renderActions(booking)}
						</div>
					</div>
				</div>

				{/* Inline invoice panel */}
				{openInvoiceId === booking.id && canReviewInvoice && (
					<StaffInvoicePanel
						booking={booking}
						sendPhone={sendToFor(booking).phone}
						sendEmail={sendToFor(booking).email}
						canSend={canSendInvoice}
						onSent={() => {
							setOpenInvoiceId(null);
							fetchBookings();
						}}
					/>
				)}
			</div>
		);
	};

	const renderEmptyState = () => {
		const filtered = !!debouncedSearch.trim() || !!selectedDate;
		if (activeTab === 'today' && !filtered) {
			const nextDay = nextBooking ? bookingDateKey(nextBooking) : '';
			return (
				<div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
					<CalendarDaysIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
						No bookings today
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						{nextBooking
							? `Your next booking is ${formatDay(nextDay, { weekday: 'long', month: 'short', day: 'numeric' })} at ${formatClock(startMinutes(nextBooking))} — ${nextBooking.customerName}.`
							: 'You have no upcoming bookings.'}
					</p>
					<button
						type="button"
						onClick={() => switchView('calendar')}
						className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 border-[#667eea] text-[#667eea] hover:bg-indigo-50 dark:hover:bg-indigo-900/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2"
					>
						<CalendarDaysIcon className="w-4 h-4" aria-hidden="true" />
						Open calendar
					</button>
				</div>
			);
		}
		return (
			<div className="text-center py-16">
				<CalendarDaysIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
					No bookings found
				</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400">
					{filtered
						? 'Try a different search or clear the date filter.'
						: `No ${activeTab} bookings to show`}
				</p>
			</div>
		);
	};

	const dateDisabled = view === 'list' && activeTab === 'today';
	const dateValue = dateDisabled ? '' : selectedDate;

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bookings</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					View and manage your scheduled Bookings
				</p>
			</div>

			{/* Filter Bar */}
			<div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm mb-5 flex gap-3 items-center flex-wrap">
				<div className="flex-1 min-w-[200px] relative">
					<MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
					<input
						type="search"
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Search by client name or service..."
						aria-label="Search bookings by client name or service"
						className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors"
					/>
				</div>
				<div className="relative">
					<input
						type="date"
						value={dateValue}
						onChange={e => onDateChange(e.target.value)}
						disabled={dateDisabled}
						aria-label={view === 'calendar' ? 'Go to date' : 'Filter by date'}
						className={`px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors ${dateDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
					/>
					{dateValue && (
						<button
							type="button"
							onClick={() => setSelectedDate('')}
							aria-label="Clear date"
							title="Clear date"
							className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-500 text-white hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2"
						>
							<XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
						</button>
					)}
				</div>
			</div>

			{/* Tabs (filter the list only) + view toggle */}
			<div className="flex flex-wrap items-center gap-3 mb-5">
				{view === 'list' ? (
					<div
						className="flex gap-2 overflow-x-auto max-w-full"
						role="tablist"
						aria-label="Booking status"
					>
						{TABS.map(tab => (
							<button
								key={tab.key}
								type="button"
								role="tab"
								aria-selected={activeTab === tab.key}
								onClick={() => {
									setActiveTab(tab.key);
									if (tab.key === 'today') setSelectedDate('');
								}}
								className={`px-6 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${
									activeTab === tab.key
										? 'text-white border-transparent'
										: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[#667eea] hover:text-[#667eea]'
								}`}
								style={activeTab === tab.key ? { background: GRADIENT } : undefined}
							>
								{tab.label}
								{tabCounts[tab.key] > 0 && (
									<span
										className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold ${
											activeTab === tab.key
												? 'bg-white/20 text-white'
												: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
										}`}
									>
										{tabCounts[tab.key]}
									</span>
								)}
							</button>
						))}
					</div>
				) : (
					<p className="text-sm text-gray-500 dark:text-gray-400">
						The calendar shows every status. Status tabs filter the list only.
					</p>
				)}
				<div
					className="ml-auto flex w-full sm:w-auto gap-1 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-1"
					role="group"
					aria-label="View"
				>
					{(
						[
							['list', 'List', ListBulletIcon],
							['calendar', 'Calendar', CalendarDaysIcon],
						] as const
					).map(([key, label, Icon]) => (
						<button
							key={key}
							type="button"
							aria-pressed={view === key}
							onClick={() => switchView(key)}
							className={`flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] ${
								view === key
									? 'bg-indigo-50 dark:bg-indigo-900/40 text-[#5a4fcf] dark:text-indigo-200'
									: 'text-gray-600 dark:text-gray-300 hover:text-[#667eea]'
							}`}
						>
							<Icon className="w-4 h-4" aria-hidden="true" />
							{label}
						</button>
					))}
				</div>
			</div>

			{view === 'calendar' ? (
				<BookingsCalendar
					bookings={calendarBookings}
					salons={salons}
					nextBooking={nextBooking}
					isLoading={isCalendarLoading}
					error={calendarError}
					onRetry={fetchCalendar}
					onDismissError={() => setCalendarError(null)}
					prefs={calendarPrefs}
					onPrefsChange={updateCalendarPrefs}
					canReviewInvoice={canReviewInvoice}
					onShowInList={showInList}
					onOpenInvoice={openInvoiceFromCalendar}
				/>
			) : (
				<>
					{/* Error Alert */}
					{error && (
						<Alert
							type="warning"
							icon={<ExclamationTriangleIcon className="w-8 h-8" />}
							title="Unable to Load Bookings"
							message={error}
							actions={[{ label: 'Retry', onClick: fetchBookings, primary: true }]}
							onDismiss={() => setError(null)}
						/>
					)}

					{/* Loading State */}
					{isLoading && (
						<div className="py-12">
							<AppLoader message="Loading bookings" variant="inline" />
						</div>
					)}

					{/* Bookings List */}
					{!isLoading && !error && (
						<div className="flex flex-col gap-3">
							{bookings.length === 0
								? renderEmptyState()
								: bookings.map(booking => renderBookingCard(booking))}

							{bookings.length > 0 && (hasMore || loadMoreError) && (
								<div className="flex flex-wrap items-center justify-between gap-3 px-1.5 pt-1 text-sm text-gray-500 dark:text-gray-400">
									<span aria-live="polite">
										Showing {bookings.length} of {total} {activeTab} bookings
									</span>
									<span className="flex items-center gap-3">
										{loadMoreError && (
											<span className="text-red-600 dark:text-red-400">
												{loadMoreError}
											</span>
										)}
										<button
											type="button"
											onClick={loadMore}
											disabled={isLoadingMore}
											className="rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-bold text-[#5a4fcf] dark:text-indigo-300 hover:border-[#667eea] disabled:cursor-wait disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea]"
										>
											{isLoadingMore
												? 'Loading…'
												: loadMoreError
													? 'Retry'
													: 'Load more'}
										</button>
									</span>
								</div>
							)}
						</div>
					)}
				</>
			)}

			{/* Booking Detail Modal */}
			<BookingDetailModal
				bookingId={selectedBookingId}
				onClose={() => setSelectedBookingId(null)}
			/>
		</AppLayout>
	);
};

export default StaffBookings;
